import { supabase } from '../services/supabase';

/**
 * Is `username` free for page `exceptId` (or for a new page when omitted)?
 *
 * Signed in, this is the is_username_available RPC (SECURITY DEFINER): it sees
 * every page, including inactive ones a creator's RLS hides, and the
 * reserved_usernames list. Signed out, that RPC isn't granted to anon
 * (permission denied, 42501), so only then it falls back to a plain lookup;
 * the database trigger still refuses a reserved name when the page is saved
 * (see isUsernameTakenError). Any other failure returns null (unknown), never
 * a guess that could show a reserved or hidden name as available.
 */
export async function isUsernameAvailable(username: string, exceptId?: string | null): Promise<boolean | null> {
  const name = username.trim().toLowerCase();
  const { data, error } = await supabase.rpc('is_username_available', {
    p_username: name,
    p_except: exceptId ?? null,
  });
  if (!error && typeof data === 'boolean') return data;
  if (error?.code !== '42501') return null;

  let query = supabase.from('talent_profiles').select('id').eq('username', name);
  if (exceptId) query = query.neq('id', exceptId);
  const { data: row, error: lookupError } = await query.maybeSingle();
  if (lookupError) return null;
  return !row;
}

/** True when a talent_profiles write failed because the username is taken or reserved. */
export function isUsernameTakenError(error: { message?: string } | null | undefined): boolean {
  return /idx_talent_profiles_username|username already taken/i.test(error?.message ?? '');
}
